import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PiChartPieSlice, PiPencilSimple, PiPlus, PiWarning } from 'react-icons/pi';
import AccountPageHeader from '../components/AccountPageHeader';
import PageIntro from '../components/PageIntro';
import PeriodNavigator from '../components/PeriodNavigator';
import PeriodContentTransition from '../components/PeriodContentTransition';
import BudgetProgress from '../components/BudgetProgress';
import BudgetEditorSheet from '../components/BudgetEditorSheet';
import SensitiveMoney, { SensitiveMoneyGroup } from '../components/SensitiveMoney';
import { useBudget } from '../hooks/useBudget';
import { useTransactions } from '../hooks/useTransactions';
import { useCategories } from '../hooks/useCategories';
import { useFeedback } from '../FeedbackContext';
import { remoteOperationMessage } from '../services/connectivityService';
import { getCategoryIcon } from '../utils/categoryPresentation';
import {
  calculateBudgetUsage,
  deriveBudgetInsights,
} from '../domain/finance';
import {
  compareYearMonthKeys,
  formatYearMonthLabel,
  fromYearMonthParam,
  getCurrentYearMonthKey,
  toYearMonthParam,
} from '../utils/period';
import './Planning.css';

export const insightContent = (insight) => {
  if (insight.type === 'exceeded') return (
    <>O orçamento mensal foi ultrapassado em <SensitiveMoney valueCents={insight.amountCents} />.</>
  );
  if (insight.type === 'category-exceeded') return `${insight.category} ultrapassou o limite definido.`;
  if (insight.type === 'closest-category') return `${insight.category} está em ${insight.percentage}% do limite.`;
  if (insight.type === 'most-consumed') return `${insight.category} é a categoria com maior valor comprometido.`;
  if (insight.type === 'pending') return (
    <><SensitiveMoney valueCents={insight.amountCents} /> estão comprometidos em despesas pendentes.</>
  );
  return '';
};

export const PlanningBudgetUsageHeadline = ({ committedExpenseCents, limitCents }) => (
  <SensitiveMoneyGroup as="h2" className="planning-summary__usage nf-financial-value" variant="hero">
    <SensitiveMoney valueCents={committedExpenseCents} variant="hero" />
    <small>de <SensitiveMoney valueCents={limitCents} variant="compact" /> usados</small>
  </SensitiveMoneyGroup>
);
const Planning = ({ currentAccountId, currentRole }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPeriod = getCurrentYearMonthKey();
  const [selectedPeriod, setSelectedPeriod] = useState(
    () => fromYearMonthParam(searchParams.get('period')) || currentPeriod
  );
  const [periodDirection, setPeriodDirection] = useState('forward');
  const [editor, setEditor] = useState(null);
  const { showSuccess, showError } = useFeedback();
  const { categories, categoryNames, expenseCategories } = useCategories(currentAccountId);
  const {
    transactions,
    loading: transactionsLoading,
    loadingMore,
    hasMore,
    error: transactionsError,
    loadMore,
  } = useTransactions(currentAccountId, selectedPeriod, { complete: true });
  const {
    budget,
    loading: budgetLoading,
    error: budgetError,
    saveMonthlyLimit,
    saveCategoryLimit,
    removeCategoryLimit,
    deleteBudget,
  } = useBudget(currentAccountId, selectedPeriod, categories);

  useEffect(() => {
    if (budget && hasMore && !loadingMore) {
      loadMore().catch(() => {});
    }
  }, [budget, hasMore, loadMore, loadingMore, transactions.length]);

  const usage = useMemo(
    () => calculateBudgetUsage({ transactions, budget, validCategories: categoryNames }),
    [transactions, budget, categoryNames]
  );
  const insights = useMemo(
    () => deriveBudgetInsights(usage).filter((item, index, items) => (
      item.type !== 'most-consumed' || !items.some((candidate) => candidate.category === item.category && candidate.type !== 'most-consumed')
    )).slice(0, 3),
    [usage]
  );
  const canEdit = ['owner', 'admin', 'editor'].includes(currentRole);
  const configuredCategories = Object.keys(budget?.categoryLimits || {});
  const availableCategories = expenseCategories.map((category) => category.name).filter((category) => !configuredCategories.includes(category));
  const monthLabel = formatYearMonthLabel(selectedPeriod, { includeYear: false })?.toLocaleLowerCase('pt-BR') || 'mês';
  const loading = transactionsLoading || budgetLoading;
  const error = transactionsError || budgetError;

  const handlePeriodChange = (nextPeriod) => {
    setPeriodDirection(compareYearMonthKeys(nextPeriod, selectedPeriod) < 0 ? 'backward' : 'forward');
    setSelectedPeriod(nextPeriod);
    const periodParam = toYearMonthParam(nextPeriod);
    if (!periodParam) return;

    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set('period', periodParam);
      return nextParams;
    }, { replace: true });
  };
  const saveMonthly = async ({ limitCents }) => {
    try {
      await saveMonthlyLimit(limitCents);
      showSuccess(
        budget ? 'O limite geral foi atualizado.' : 'Seu orçamento mensal foi criado.',
        budget ? 'Orçamento atualizado' : 'Orçamento criado'
      );
    } catch (saveError) {
      showError(remoteOperationMessage(saveError, 'Não foi possível salvar o orçamento. Tente novamente.'));
      throw saveError;
    }
  };

  const saveCategory = async ({ category, limitCents }) => {
    try {
      const existed = Object.prototype.hasOwnProperty.call(budget?.categoryLimits || {}, category);
      await saveCategoryLimit(category, limitCents);
      showSuccess(
        existed ? `O limite de ${category} foi atualizado.` : `O limite de ${category} foi criado.`,
        existed ? 'Limite atualizado' : 'Limite criado'
      );
    } catch (saveError) {
      showError(remoteOperationMessage(saveError, 'Não foi possível salvar o limite da categoria.'));
      throw saveError;
    }
  };

  const removeCategory = async (category) => {
    try {
      await removeCategoryLimit(category);
      showSuccess(`O limite de ${category} foi removido.`, 'Limite removido');
    } catch (removeError) {
      showError(remoteOperationMessage(removeError, 'Não foi possível remover o limite da categoria.'));
      throw removeError;
    }
  };

  const removeBudget = async () => {
    try {
      await deleteBudget();
      showSuccess('Os limites deste mês foram removidos. Suas movimentações foram preservadas.', 'Orçamento removido');
    } catch (deleteError) {
      showError(remoteOperationMessage(deleteError, 'Não foi possível excluir o orçamento.'));
      throw deleteError;
    }
  };

  return (
    <div className="planning-page">
      <AccountPageHeader title="Planejamento" onBack={() => navigate(-1)} wide />
      <div className="planning-page__container">
        <PageIntro className="planning-page__description" description="Defina limites e acompanhe quanto ainda pode gastar neste mês." />
        <PeriodNavigator value={selectedPeriod} currentPeriod={currentPeriod} onChange={handlePeriodChange} />

        {error && !loading ? (
          <section className="nf-data-error" role="alert">
            <strong>Não foi possível carregar o planejamento.</strong>
            <span>Verifique a conexão com o Firestore e tente novamente.</span>
            <button type="button" onClick={() => window.location.reload()}>Tentar novamente</button>
          </section>
        ) : (
          <PeriodContentTransition
            loading={loading}
            period={selectedPeriod}
            direction={periodDirection}
            label={`Atualizando ${monthLabel}...`}
          >
          <main className="planning-page__content">
            {!budget ? (
              <section className="planning-empty">
                <div className="planning-empty__icon"><PiChartPieSlice aria-hidden="true" /></div>
                <div>
                  <span>Planejamento mensal</span>
                  <h2>Crie seu orçamento de <span className="planning-empty__month">{monthLabel}</span></h2>
                  <p>Defina um limite geral. Os valores usados serão calculados automaticamente a partir das despesas deste período.</p>
                </div>
                {canEdit ? (
                  <button type="button" className="planning-button planning-button--primary" onClick={() => setEditor({ mode: 'monthly' })}>
                    Criar orçamento
                  </button>
                ) : <small>Você possui acesso somente para leitura.</small>}
              </section>
            ) : (
              <>
                <section className={`planning-summary planning-summary--${usage.monthly.state}`}>
                  <div className="planning-summary__header">
                    <div>
                      <span>Orçamento de {monthLabel}</span>

                      <PlanningBudgetUsageHeadline
                        committedExpenseCents={usage.committedExpenseCents}
                        limitCents={usage.monthly.limitCents}
                      />
                    </div>
                    {canEdit && (
                      <button type="button" className="planning-icon-button" onClick={() => setEditor({ mode: 'monthly' })} aria-label="Editar orçamento mensal">
                        <PiPencilSimple aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  <div className="planning-summary__progress-label">
                    <strong>{usage.monthly.percentage}% utilizado</strong>
                    <SensitiveMoneyGroup className="nf-financial-value">
                      <SensitiveMoney valueCents={usage.monthly.remainingCents} absolute /> {usage.monthly.remainingCents < 0 ? 'acima do limite' : 'disponíveis'}
                    </SensitiveMoneyGroup>
                  </div>
                  <BudgetProgress percentage={usage.monthly.percentage} state={usage.monthly.state} label={`${usage.monthly.percentage}% do orçamento mensal utilizado`} />
                  <div className="planning-summary__metrics">
                    <div><span>Realizado</span><SensitiveMoney as="strong" className="nf-financial-value" valueCents={usage.realizedExpenseCents} semantic="expense" /><small>despesas pagas</small></div>
                    <div><span>Comprometido</span><SensitiveMoney as="strong" className="nf-financial-value" valueCents={usage.committedExpenseCents} semantic="expense" /><small>pagas + pendentes</small></div>
                    <div><span>Pendente</span><SensitiveMoney as="strong" className="nf-financial-value" valueCents={usage.pendingExpenseCents} semantic="expense" /><small>ainda não pago</small></div>
                  </div>
                </section>

                {insights.length > 0 && (
                  <section className="planning-insights" aria-labelledby="planning-insights-title">
                    <div className="planning-section-heading">
                      <div><span>Leitura do mês</span><h2 id="planning-insights-title">Pontos de atenção</h2></div>
                    </div>
                    <div className="planning-insights__list">
                      {insights.map((insight) => (
                        <p key={`${insight.type}-${insight.category || 'month'}`} className={insight.type.includes('exceeded') ? 'is-danger' : ''}>
                          <PiWarning aria-hidden="true" />
                          <span className="planning-insight__content">{insightContent(insight)}</span>
                        </p>
                      ))}
                    </div>
                  </section>
                )}

                <section className="planning-categories" aria-labelledby="planning-categories-title">
                  <div className="planning-section-heading">
                    <div><span>Por categoria</span><h2 id="planning-categories-title">Limites deste mês</h2></div>
                    {canEdit && availableCategories.length > 0 && (
                      <button type="button" className="planning-button planning-button--secondary" onClick={() => setEditor({ mode: 'category' })}>
                        <PiPlus aria-hidden="true" /> Adicionar limite
                      </button>
                    )}
                  </div>

                  {usage.categories.length ? (
                    <div className="planning-category-list">
                      {usage.categories.map((category, index) => {
                        const categoryDefinition = categories.find((item) => item.name === category.category);
                        const Icon = getCategoryIcon({ name: category.category, icon: categoryDefinition?.icon });
                        return (
                          <article className={`planning-category planning-category--${category.state}`} key={category.category}>
                            <div className={`planning-category__icon is-tone-${index % 5}`}><Icon aria-hidden="true" /></div>
                            <div className="planning-category__content">
                              <div className="planning-category__heading">
                                <div><h3>{category.category}</h3><SensitiveMoneyGroup as="p"><SensitiveMoney as="strong" valueCents={category.committedExpenseCents} /> de <SensitiveMoney valueCents={category.limitCents} /></SensitiveMoneyGroup></div>
                                <div className="planning-category__usage"><strong>{category.percentage}%</strong><span>utilizado</span></div>
                              </div>
                              <BudgetProgress percentage={category.percentage} state={category.state} label={`${category.category}: ${category.percentage}% do limite utilizado`} />
                              <div className="planning-category__footer">
                                <SensitiveMoneyGroup>Realizado: <SensitiveMoney valueCents={category.realizedExpenseCents} /></SensitiveMoneyGroup>
                                <SensitiveMoneyGroup><SensitiveMoney valueCents={category.remainingCents} absolute /> {category.remainingCents < 0 ? 'acima do limite' : 'disponíveis'}</SensitiveMoneyGroup>
                              </div>
                            </div>
                            {canEdit && (
                              <button type="button" className="planning-icon-button" onClick={() => setEditor({ mode: 'category', category: category.category, limitCents: category.limitCents })} aria-label={`Editar limite de ${category.category}`}>
                                <PiPencilSimple aria-hidden="true" />
                              </button>
                            )}
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="planning-categories__empty">
                      <p>Você ainda não definiu limites por categoria.</p>
                      {canEdit && availableCategories.length > 0 && (
                        <button type="button" onClick={() => setEditor({ mode: 'category' })}>Adicionar primeiro limite</button>
                      )}
                    </div>
                  )}
                </section>
              </>
            )}
            {loadingMore && <p className="planning-page__syncing" role="status">Atualizando totais do período…</p>}
          </main>
          </PeriodContentTransition>
        )}
      </div>

      {editor && (
        <BudgetEditorSheet
          mode={editor.mode}
          monthlyLimitCents={budget?.monthlyLimitCents || 0}
          categories={editor.category ? expenseCategories.map((category) => category.name) : availableCategories}
          initialCategory={editor.category || ''}
          initialLimitCents={editor.limitCents || 0}
          canDelete={editor.mode === 'monthly' ? Boolean(budget) : Boolean(editor.category)}
          onSave={editor.mode === 'monthly' ? saveMonthly : saveCategory}
          onDelete={editor.mode === 'monthly' ? removeBudget : () => removeCategory(editor.category)}
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
};

export default Planning;
